// PRD.282 — Clinic Type-Driven Patient Workspace Defaults
//
// Clinic classification (primary clinic type + optional additional specialties)
// is stored under `app_config.extra_json.clinic_classification`, mirroring the
// established `regional_settings` pattern (see app-config.ts). It rides the
// existing PowerSync + SyncWrite `extra_json` path, so no schema/migration or
// write-path changes are required.
//
// Storage stores STABLE LOWERCASE CODES only — never UI labels. All reads
// normalize/validate so an unknown or malformed value can never crash a page:
// an invalid primary falls back to 'general' and unknown specialties are
// dropped.
//
// This module is intentionally self-contained (no workspace-package imports) so
// it can run under the node:test harness. The `app_config` row is accepted via
// a structural shape that AppConfigRow satisfies.

// ─── Codes ───

export type PrimaryClinicType = 'general' | 'dermatology_aesthetic' | 'dental' | 'other';

export type ClinicSpecialty =
  | 'general_medicine'
  | 'dermatology'
  | 'aesthetic_medicine'
  | 'dental'
  | 'pediatrics'
  | 'ent'
  | 'ob_gyn'
  | 'other';

/** Supported primary clinic types, in display order. */
export const PRIMARY_CLINIC_TYPES: readonly PrimaryClinicType[] = [
  'general',
  'dermatology_aesthetic',
  'dental',
  'other',
] as const;

/** Supported additional specialties, in display order. */
export const CLINIC_SPECIALTIES: readonly ClinicSpecialty[] = [
  'general_medicine',
  'dermatology',
  'aesthetic_medicine',
  'dental',
  'pediatrics',
  'ent',
  'ob_gyn',
  'other',
] as const;

/** Stable code → display label. Labels are UI-only and never persisted. */
export const PRIMARY_CLINIC_TYPE_LABELS: Record<PrimaryClinicType, string> = {
  general: 'General',
  dermatology_aesthetic: 'Dermatology / Aesthetic',
  dental: 'Dental',
  other: 'Other',
};

export const CLINIC_SPECIALTY_LABELS: Record<ClinicSpecialty, string> = {
  general_medicine: 'General Medicine',
  dermatology: 'Dermatology',
  aesthetic_medicine: 'Aesthetic Medicine',
  dental: 'Dental',
  pediatrics: 'Pediatrics',
  ent: 'ENT',
  ob_gyn: 'OB-GYN',
  other: 'Other',
};

/**
 * Specialties that a primary clinic type already implies, so the UI does not
 * ask an administrator to re-select them and the write layer de-duplicates
 * them out of `additional_specialties` (PRD §1.2 / Validation Rules).
 */
const PRIMARY_IMPLIED_SPECIALTIES: Record<PrimaryClinicType, ClinicSpecialty[]> = {
  general: ['general_medicine'],
  dermatology_aesthetic: ['dermatology', 'aesthetic_medicine'],
  dental: ['dental'],
  other: ['other'],
};

// ─── Classification value ───

export interface ClinicClassification {
  primaryClinicType: PrimaryClinicType;
  additionalSpecialties: ClinicSpecialty[];
}

/** Backfill default for existing clinics (PRD §10). */
export const DEFAULT_CLINIC_CLASSIFICATION: ClinicClassification = {
  primaryClinicType: 'general',
  additionalSpecialties: [],
};

/**
 * Specialties already implied by a primary clinic type — the UI hides these
 * from the "Additional specialties" picker so they aren't selected twice.
 */
export function getImpliedSpecialties(primary: PrimaryClinicType): ClinicSpecialty[] {
  return [...(PRIMARY_IMPLIED_SPECIALTIES[primary] ?? [])];
}

export function isPrimaryClinicType(v: unknown): v is PrimaryClinicType {
  return typeof v === 'string' && (PRIMARY_CLINIC_TYPES as readonly string[]).includes(v);
}

export function isClinicSpecialty(v: unknown): v is ClinicSpecialty {
  return typeof v === 'string' && (CLINIC_SPECIALTIES as readonly string[]).includes(v);
}

/**
 * Normalize an arbitrary/untrusted classification into a valid, canonical one:
 *  - primary falls back to 'general' when missing/invalid,
 *  - specialties are filtered to supported codes,
 *  - specialties already implied by the primary type are removed,
 *  - duplicates are removed (stable first-seen order preserved).
 *
 * Pure and deterministic — safe to call on read and before every write.
 */
export function normalizeClinicClassification(raw: unknown): ClinicClassification {
  const obj = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const primaryRaw = obj.primaryClinicType ?? obj.primary_clinic_type;
  const primaryClinicType: PrimaryClinicType = isPrimaryClinicType(primaryRaw)
    ? primaryRaw
    : 'general';

  const specialtiesRaw = obj.additionalSpecialties ?? obj.additional_specialties;
  const implied = new Set<ClinicSpecialty>(PRIMARY_IMPLIED_SPECIALTIES[primaryClinicType]);
  const seen = new Set<ClinicSpecialty>();
  const additionalSpecialties: ClinicSpecialty[] = [];
  if (Array.isArray(specialtiesRaw)) {
    for (const s of specialtiesRaw) {
      if (!isClinicSpecialty(s)) continue; // unsupported code — drop
      if (implied.has(s)) continue; // already covered by the primary type
      if (seen.has(s)) continue; // duplicate
      seen.add(s);
      additionalSpecialties.push(s);
    }
  }
  return { primaryClinicType, additionalSpecialties };
}

// ─── Read / write against app_config.extra_json ───

/** Structural shape of the source row — AppConfigRow satisfies this. */
export interface ClinicClassificationSource {
  extra_json?: string | null;
}

/**
 * Parse `extra_json` (text from PowerSync SQLite, object from server) into a
 * plain object. Falls back to {} on any malformed input. Kept local so this
 * module stays free of workspace-package imports (node:test compatibility).
 */
function parseExtraJson(extraJson: unknown): Record<string, unknown> {
  if (extraJson == null) return {};
  if (typeof extraJson === 'object' && !Array.isArray(extraJson)) {
    return extraJson as Record<string, unknown>;
  }
  if (typeof extraJson === 'string' && extraJson.trim().length > 0) {
    try {
      const parsed = JSON.parse(extraJson);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
    } catch {
      /* fall through */
    }
  }
  return {};
}

/**
 * Extract the clinic classification from an app_config row, applying the
 * `general` backfill default when nothing has been saved. Never throws.
 */
export function getClinicClassification(
  row: ClinicClassificationSource | null | undefined,
): ClinicClassification {
  const extra = parseExtraJson(row?.extra_json);
  return normalizeClinicClassification(extra.clinic_classification);
}

/**
 * Serialize a classification into an updated `extra_json` JSON string,
 * preserving every other top-level key (regional_settings, etc.). The value is
 * normalized so only valid, de-duplicated codes are persisted.
 */
export function writeClinicClassification(
  currentExtraJson: unknown,
  classification: ClinicClassification,
): string {
  const normalized = normalizeClinicClassification(classification);
  const next = { ...parseExtraJson(currentExtraJson) };
  next.clinic_classification = normalized;
  return JSON.stringify(next);
}
